import { Box, Typography, Paper } from "@mui/material"
import { useEffect, useRef } from "react"
import { LocationProperty } from "types/Property"
import type { Location } from "types/Location";
import { CoordinateEditor } from './CoordinateEditor';
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Graphic from "@arcgis/core/Graphic";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Expand from "@arcgis/core/widgets/Expand";
import Search from "@arcgis/core/widgets/Search";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Point from "@arcgis/core/geometry/Point";
import { load, project } from "@arcgis/core/geometry/projection";
import spatialConstants from "utils/constants";

export type LocationFormControlProps = {
    value?: Location,
    property: LocationProperty,
    onChange?: (newValue: Location | undefined) => void
}

export function LocationFormControl({ value, property, onChange }: LocationFormControlProps) {
    const workspaceWkid = new SpatialReference({ wkid: property.wkid ?? spatialConstants.DEFAULT_WKID });
    const defaultXStartPoint = property.initialX ?? spatialConstants.DEFAULT_X_STARTPOINT; // if not provided, default to the Aus/Nz/Pacific region as most Beca business will be here
    const defaultYStartPoint = property.initialY ?? spatialConstants.DEFAULT_Y_STARTPOINT;
    const defaultZoomLevel = property.initialZoom ?? 3;

    const mapDivRef = useRef<HTMLDivElement | null>(null);
    const viewRef = useRef<MapView | null>(null);
    const onChangeRef = useRef<LocationFormControlProps["onChange"] | undefined>(); // keep a ref to the current onchange as otherwise the clickhandler in our useEffect below could call a click handler with stale form data
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    // bootstrap map once
    useEffect(() => {
        if (!mapDivRef.current || viewRef.current) return;
        const map = new Map({ basemap: "hybrid" });
        const view = new MapView({
            container: mapDivRef.current,
            map,
            center: [defaultXStartPoint, defaultYStartPoint],
            zoom: defaultZoomLevel,
            constraints: {
                minZoom: 2,
            },
        });

        view.ui.move("zoom", "top-right");
        viewRef.current = view;

        const basemapGallery = new BasemapGallery({ view });
        const basemapExpand = new Expand({ view, content: basemapGallery, expanded: false });
        view.ui.add(basemapExpand, "top-left");

        const search = new Search({ view });
        const searchExpand = new Expand({ view, content: search, expanded: false });
        view.ui.add(searchExpand, "top-left");

        const clickHandle = view.on("click", async (event) => {
            const mapPoint = event.mapPoint;
            if (!mapPoint) return;
            const projected = await projectPoint(mapPoint);
            onChangeRef.current?.({ type: "Point", coordinates: [projected.x, projected.y] } as Location);
        });
        return () => {
            viewRef.current?.destroy();
            viewRef.current = null;
        };
        // we only want to run once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // sync graphic with current coordinates
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;

        redrawPoint(view, value);
    }, [value?.coordinates?.[0], value?.coordinates?.[1]]);

    return (
        <Box display="flex" flexDirection="column" gap={0}>
            <Typography variant="subtitle2" color="text.secondary">
                {property.displayName || property.name}
            </Typography>
            <Paper variant="outlined" sx={{ position: "relative", width: "100%", aspectRatio: "16 / 9", overflow: "hidden" }}>
                <Box ref={mapDivRef} sx={{ position: "absolute", inset: 0 }} />
            </Paper>
            <Box display="flex" alignItems="center" gap={2} sx={{ width: '100%' }}>
                <CoordinateEditor
                    value={value}
                    isGeographic={workspaceWkid.isGeographic}
                    coordinateLabel={`Coordinates ${workspaceWkid.isGeographic ? '(lat,lon)' : '(metres)'}`}
                    wkidLabel={`WKID: ${workspaceWkid.wkid}`}
                    onChange={(loc) => onChange?.(loc)}
                />
            </Box>
            <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
            </Box>
        </Box>
    );

    async function redrawPoint(view: MapView, value: Location | undefined) {
        await view.when(); // wait for view instance creation
        view.graphics.removeAll();
        if (value?.coordinates && value.coordinates.length >= 2) {
            const [x, y] = value.coordinates;
            const point = new Point({ x, y, spatialReference: workspaceWkid });
            const graphic = new Graphic({
                geometry: point,
                symbol: {
                    type: "simple-marker",
                    color: [255, 0, 0],
                    outline: { color: [255, 255, 255], width: 1.5 },
                    size: 10
                }
            });
            view.graphics.add(graphic);
            try {
                await view.goTo({target: point, zoom: 16}, { animate: true, duration: 500 }, );
            } catch (err: any) {
                console.warn("goTo failed", err);
            }
        }
    }

    async function projectPoint(point: Point): Promise<Point> {
        try {
            const currentWkid = (point.spatialReference as SpatialReference || undefined)?.wkid;
            if (!workspaceWkid?.wkid || currentWkid === workspaceWkid.wkid) {
                return point;
            }
            await load();
            const projectedPoint = project(point, workspaceWkid) as Point | null;
            if (!projectedPoint) {
                console.error("Could not convert map point to target spatial reference.");
                return point;
            }
            return projectedPoint;
        } catch (e) {
            console.error("Projection failed", e);
            return point;
        }
    }
}